import { useEffect, useState } from 'react'
import { prefersReducedMotion, useInView } from './useInView'
import { parseStatValue } from './stat'

const DURATION_MS = 1_400

/**
 * Counts up to the target when scrolled into view (Recurly-style stat band).
 * Falls back to the static value for reduced motion or unparsable stats.
 */
export function AnimatedCounter({ value, className }: { value: string; className?: string }) {
  const { ref, inView } = useInView<HTMLSpanElement>()
  const parsed = parseStatValue(value)
  const [progress, setProgress] = useState(0)

  const animate = inView && parsed !== null && !prefersReducedMotion()

  useEffect(() => {
    if (!animate) return
    let frame: number
    const start = performance.now()
    const tick = (now: number) => {
      const t = Math.min((now - start) / DURATION_MS, 1)
      // ease-out cubic
      setProgress(1 - Math.pow(1 - t, 3))
      if (t < 1) frame = requestAnimationFrame(tick)
    }
    frame = requestAnimationFrame(tick)
    return () => cancelAnimationFrame(frame)
  }, [animate])

  if (!parsed) {
    return <span className={className}>{value}</span>
  }

  const settled = !animate || progress >= 1
  const shown = settled ? parsed.value : parsed.value * progress

  return (
    <span ref={ref} className={className} aria-label={value}>
      {parsed.prefix}
      {shown.toFixed(parsed.decimals)}
      {parsed.suffix}
    </span>
  )
}
